import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./Admin.css";
import { useAuth } from "../pages/js/AuthContext";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../API/endpoint";

// Hooks personnalisés
import { useProducts } from "../hooks/useProducts";
import { useCategories } from "../hooks/useCategories";
import { useUsers } from "../hooks/useUsers";
import { useOrders } from "../hooks/useOrders";
import { useContacts } from "../hooks/useContacts";

// Composants des onglets
import { ProductsTab } from "./admin/ProductsTab";
import { CategoriesTab } from "./admin/CategoriesTab";
import { UsersTab } from "./admin/UsersTab";
import { OrdersTab } from "./admin/OrdersTab";
import { ContactsTab } from "./admin/ContactsTab";

function Admin() {
  const [activeTab, setActiveTab] = useState("products");
  const { isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  // Utilisation des hooks personnalisés
  const products = useProducts();
  const categories = useCategories();
  const users = useUsers();
  const orders = useOrders();
  const contacts = useContacts();

  // Message d'erreur global
  const [globalError, setGlobalError] = useState("");

  useEffect(() => {
    if (!isAdmin()) {
      navigate("/login");
    }
  }, [isAdmin, navigate]);

  // Charger les données au montage
  useEffect(() => {
    if (isAdmin()) {
      products.loadProducts();
      categories.loadCategories();
      users.loadUsers();
      orders.loadOrders();
      contacts.loadContacts();
    }
  }, [isAdmin]);

  // Fonction pour obtenir l'URL de la miniature
  const getThumbnailUrl = (product) => {
    if (product.thumbnailPath) {
      return `${API_BASE_URL}${product.thumbnailPath}`;
    }
    if (product.path && product.path.includes("/uploads/products/")) {
      const filename = product.path.split("/").pop();
      return `${API_BASE_URL}/uploads/thumbnails/thumb_${filename}`;
    }
    return `${API_BASE_URL}${product.path}`;
  };

  // Gestion des produits
  const handleSaveProduct = async (id, productData) => {
    if (id) {
      const actualId = productData.get ? productData.get("id") : id;
      await products.handleUpdateProduct(actualId, productData);
    } else {
      await products.handleCreateProduct(productData);
    }
  };

  const handleBulkSaveProducts = async (productsData) => {
    let successCount = 0;
    let errorCount = 0;

    for (const productData of productsData) {
      try {
        await products.handleCreateProduct(productData);
        successCount++;
      } catch (err) {
        errorCount++;
        console.error("Erreur produit:", err);
      }
    }

    setGlobalError(
      errorCount > 0
        ? `${successCount} produits ajoutés, ${errorCount} erreurs`
        : `${successCount} produits ajoutés avec succès`
    );
  };

  // Gestion des catégories
  const handleSaveCategory = async (id, categoryData) => {
    if (id) {
      const actualId = categoryData.get ? categoryData.get("id") : id;
      await categories.handleUpdateCategory(actualId, categoryData);
    } else {
      await categories.handleCreateCategory(categoryData);
    }
  };

  // Gestion des utilisateurs
  const handleSaveUser = async (id, userData) => {
    if (id) {
      await users.handleUpdateUser(id, userData);
    } else {
      await users.handleCreateUser(userData);
    }
  };

  // Affichage du message d'erreur global
  const error =
    globalError ||
    products.error ||
    categories.error ||
    users.error ||
    orders.error ||
    contacts.error;

  return (
    <main className="admin-page">
      <Helmet>
        <title>Administration - Déco Ariège</title>
        <meta name="description" content="Panneau d'administration pour gérer les produits, commandes et utilisateurs." />
      </Helmet>
      <h1>Administration</h1>

      {error && (
        <div
          className="error-message"
          style={{
            backgroundColor: "#ff4444",
            color: "white",
            padding: "10px",
            borderRadius: "5px",
            marginBottom: "20px",
          }}
        >
          {error}
        </div>
      )}

      <div className="admin-tabs">
        <button
          className={activeTab === "products" ? "active" : ""}
          onClick={() => setActiveTab("products")}
        >
          Produits
        </button>
        <button
          className={activeTab === "categories" ? "active" : ""}
          onClick={() => setActiveTab("categories")}
        >
          Catégories
        </button>
        <button
          className={activeTab === "users" ? "active" : ""}
          onClick={() => setActiveTab("users")}
        >
          Utilisateurs
        </button>
        <button
          className={activeTab === "orders" ? "active" : ""}
          onClick={() => setActiveTab("orders")}
        >
          Commandes
          {orders.pendingOrdersCount > 0 && (
            <span className="orders-badge">{orders.pendingOrdersCount}</span>
          )}
        </button>
        <button
          className={activeTab === "contacts" ? "active" : ""}
          onClick={() => setActiveTab("contacts")}
        >
          Messages
          {contacts.newContactsCount > 0 && (
            <span className="orders-badge">{contacts.newContactsCount}</span>
          )}
        </button>
      </div>

      <div className="admin-logout">
        <button
          onClick={() => {
            logout();
            navigate("/");
          }}
        >
          Déconnexion
        </button>
      </div>

      <div className="admin-content">
        {activeTab === "products" && (
          <ProductsTab
            products={products.products}
            categories={categories.categories}
            loading={products.loading}
            onSave={handleSaveProduct}
            onDelete={products.handleDeleteProduct}
            onToggleStatus={products.handleToggleStatus}
            onRotate={products.handleRotateImage}
            onBulkSave={handleBulkSaveProducts}
          />
        )}

        {activeTab === "categories" && (
          <CategoriesTab
            categories={categories.categories}
            onSave={handleSaveCategory}
            onDelete={categories.handleDeleteCategory}
          />
        )}

        {activeTab === "users" && (
          <UsersTab
            users={users.users}
            onSave={handleSaveUser}
            onDelete={users.handleDeleteUser}
          />
        )}

        {activeTab === "orders" && (
          <OrdersTab
            orders={orders.orders}
            onUpdateStatus={orders.handleUpdateOrderStatus}
            onDelete={orders.handleDeleteOrder}
            onViewDetails={orders.loadOrderDetails}
            getThumbnailUrl={getThumbnailUrl}
          />
        )}

        {activeTab === "contacts" && (
          <ContactsTab
            contacts={contacts.contacts}
            onDelete={contacts.handleDeleteContact}
            onMarkAsRead={contacts.handleMarkAsRead}
          />
        )}
      </div>
    </main>
  );
}

export default Admin;
